import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useCreateMutation } from "../../../api/genericHooks";
import { BookContext } from "../../../contexts/BookContext";
import DateInput from "../../Inputs/DateInput";
import { Step } from "../GenerateBookPDFModal";
import { GenPDFStepper } from "./GenPDFStepper";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  steps: Step[];
  nextStep: () => void;
};

export type GenerateBookPDFData = {
  title: string;
  author_birthdate: string;
  publication_date: string;
};

export const GenerateBookPDFModalStep3: FC<Props> = ({
  open,
  setOpen,
  steps,
  nextStep,
}) => {
  const { t } = useTranslation();
  const { book, refresh } = useContext(BookContext);
  const [birthDateValue, setBirthDateValue] = useState(
    book?.author_birthdate || ""
  );
  const [publicationDateValue, setPublicationDateValue] = useState(
    book?.publication_date || ""
  );

  // Form Methods =============================================================
  const GeneratePDF1FormSchema = Yup.object().shape({
    title: Yup.string().required(t("errors.required")),
    author_birthdate: Yup.string().required(t("errors.required")),
    publication_date: Yup.string().required(t("errors.required")),
  });

  const defaultValues = useMemo<GenerateBookPDFData>(() => {
    return {
      title: book?.title || "",
      author_birthdate: birthDateValue,
      publication_date: publicationDateValue,
    };
  }, [book]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<GenerateBookPDFData>({
    defaultValues: defaultValues,
    resolver: yupResolver(GeneratePDF1FormSchema),
  });

  // Mutations ===============================================================
  const generateBookPDFMutation = useCreateMutation("/update_book_infos");

  // Form Submit =============================================================
  const onSubmit = useCallback(
    (values: GenerateBookPDFData) => {
      const data = { book: values };
      generateBookPDFMutation.mutateAsync(data).then(() => {
        toast.success(t("generateBookPDFModal.bookInfoSaveSuccess"));
        refresh();
        nextStep();
      });
    },
    [refresh, t]
  );

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-full mx-2 sm:mx-0"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0"
                  >
                    {t("generateBookPDFModal.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />

                  <GenPDFStepper steps={steps} />

                  <div className="flex flex-row mt-5">
                    <div className="w-full sm:w-2/3 sm:mx-auto">
                      <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="col-span-full">
                          <label
                            htmlFor="title"
                            className="block text-sm font-medium text-slate-900 text-left"
                          >
                            {t("generateBookPDFModal.bookTitle")}
                          </label>
                          <div className="mt-2 text-left">
                            <input
                              id="title"
                              type="text"
                              {...register("title", { required: true })}
                              autoComplete="title"
                              maxLength={26}
                              className="block w-full rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600 text-left"
                            />
                          </div>
                          {errors.title && (
                            <span className="text-rust-400 font-nunito text-sm block text-left">
                              {errors.title.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="col-span-full sm:col-span-3">
                          <div className="mt-2 text-left">
                            <DateInput
                              id="author_birthdate"
                              label={t("generateBookPDFModal.authorBirthdate")}
                              setValue={setValue}
                              dateValue={birthDateValue}
                              setDateValue={setBirthDateValue}
                            />
                          </div>
                        </div>
                        <div className="col-span-full sm:col-span-3">
                          <div className="mt-2 text-left">
                            <DateInput
                              id="publication_date"
                              label={t("generateBookPDFModal.publicationDate")}
                              setValue={setValue}
                              dateValue={publicationDateValue}
                              setDateValue={setPublicationDateValue}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3 sm:gap-0">
                <button
                  type="submit"
                  className="inline-flex w-full justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:w-auto"
                >
                  {t("addPicturesModal.save")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
